import { CUSTOM_ELEMENTS_SCHEMA, NgModule, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppComponent } from './app.component';
import { Router, RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HeaderComponent } from './layout/header/header.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { appInitializer } from './initialize.function';
import { CellTooltipDirective } from './common/directives/cell-tooltip.directive';
import { AuthenticationService } from './services/authentication.service';
import { ToastrModule } from 'ngx-toastr';
import { AppInterceptorService } from './services/app-interceptor.service';
import { OverlayService } from '@nucleus/ui-common';
import { GlassPanelComponent } from './glass-panel/glass-panel.component';
import { CookieService } from 'ngx-cookie-service';
import { FormsModule } from '@angular/forms';
import { InitService } from './services/init/init.service';
import { UserService } from './services/user/user.service';
import { AuditService } from './services/audit/audit.service';
import { LoadingService } from './services/init/loading-service';
import { FilterAutocompleteComponent } from "@nucleus/ui-base";
import { MenuNavResetDirective } from "@nucleus/ui-base";
import { BreadcrumbsComponent } from "@nucleus/ui-base";
import { NgIdleModule } from '@ng-idle/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { GANTT_GLOBAL_CONFIG, GanttI18nLocale, NgxGanttModule } from '@worktile/gantt';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { MatSelectModule } from '@angular/material/select';
import { FooterComponent } from './layout/footer/footer.component';
import { AddressComponent } from './common/address/address.component';
import { AddressApiModule, BASE_PATH as AddressBASE_PATH } from './api/address-service';
import { provideUserClient } from '@nucleus/user-ui';
import { RequestHandler } from './services/request-handler';
import { FilterSingleEnumAutocompleteComponent } from '@nucleus/ui-common';
import { provideNucleusUiBase, CAPABILITY_CHECKER } from '@nucleus/ui-base';
import { CapabilityCheckerService } from './services/permission/capability-checker.service';
import { DefaultFileViewer } from '@nucleus/file-viewer-ui';
import { ApplicationConfigService } from './services/application-config.service';
import { PermissionCheckerService } from './services/permission/permission-checker.service';
import { UIConfigurationService } from './services/ui-configuration.service';
import { NUCLEUS_THEME_STORAGE_KEY } from '@nucleus/ui-common';
import { AccountingApiModule, BASE_PATH as AccountingBASE_PATH   } from './api/accounting-service';
import { StatementApiModule, BASE_PATH as StatementgBASE_PATH   } from './api/statement-service';
import { CategorizerApiModule, BASE_PATH as CategorizerBASE_PATH   } from './api/categorizer-service';
import { ExpenseApiModule, BASE_PATH as ExpenseBASE_PATH   } from './api/expense-service';
import { AuthenticationApiModule, BASE_PATH as AuthenticationBASE_PATH   } from './api/authentication-service';
import { TransactionApiModule, BASE_PATH as TransactionBASE_PATH   } from './api/transaction-service';
import { UiMessageBrokerApiModule, BASE_PATH as UIMessageBrokerBASE_PATH } from './api/ui-message-broker';
import { provideSystemPropertyClient, WORKFLOW_SECRET_NOTIFIER } from '@nucleus/system-property-ui';
import { BookwiseWorkflowSecretNotifierAdapter } from './administration/workflow-secrets/workflow-secret.adapter';
import { provideAuditClient } from '@nucleus/audit-report-ui';
import { provideMailClient } from '@nucleus/mail-report-ui';
import { MonitoringApiModule, BASE_PATH as MonitoringBASE_PATH } from './api/monitoring-service';
import { provideReferenceDataClient } from '@nucleus/reference-data-ui';
import { IntegrationApiModule, BASE_PATH as IntegrationBASE_PATH } from './api/integration-service';
import { provideSecurityGatewayClient } from '@nucleus/security-admin-ui';
import { PerformanceApiModule, BASE_PATH as PerformanceBASE_PATH } from './api/performance-service';
import { ConnectorsApiModule, BASE_PATH as ConnectorsBASE_PATH } from './api/connectors-service';

@NgModule({ declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent
    ],
    bootstrap: [AppComponent], 
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CellTooltipDirective,
        BrowserModule,
        RouterModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatIconModule,
        MatMenuModule,
        MatToolbarModule,
        MatButtonModule, 
        MatTooltipModule,
        FilterAutocompleteComponent,
        FilterSingleEnumAutocompleteComponent,
        MenuNavResetDirective,
        BreadcrumbsComponent,
        AddressComponent,
        CommonModule,
        NgIdleModule.forRoot(),
        GlassPanelComponent,
        FormsModule,
        MatSelectModule,
        MatFormFieldModule,
        NgxChartsModule,
        NgxGanttModule,
        NgxMatTimepickerModule,
        ToastrModule.forRoot({
            closeButton: true,
            timeOut: 5000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true
        }),
        ], providers: [
        ...provideNucleusUiBase({
            applicationConfig: ApplicationConfigService,
            permissionChecker: PermissionCheckerService,
            uiConfiguration:   UIConfigurationService,
            fileViewer:        DefaultFileViewer,
        }),
        { provide: NUCLEUS_THEME_STORAGE_KEY, useValue: 'bw-theme' },
        provideAppInitializer(() => {
        const initializerFn = (appInitializer)(inject(AuthenticationService), inject(LoadingService), inject(InitService), inject(UserService), inject(Router), inject(PermissionCheckerService), inject(ApplicationConfigService));
        return initializerFn();        }),
        importProvidersFrom(AddressApiModule),
        provideUserClient(),
        importProvidersFrom(AccountingApiModule),
        importProvidersFrom(StatementApiModule),
        importProvidersFrom(CategorizerApiModule),
        importProvidersFrom(ExpenseApiModule),
        importProvidersFrom(AuthenticationApiModule),
        importProvidersFrom(TransactionApiModule),
        importProvidersFrom(UiMessageBrokerApiModule),
        provideSystemPropertyClient(),
        { provide: WORKFLOW_SECRET_NOTIFIER, useClass: BookwiseWorkflowSecretNotifierAdapter },
        provideAuditClient(),
        provideMailClient(),
        { provide: CAPABILITY_CHECKER, useExisting: CapabilityCheckerService },
        importProvidersFrom(MonitoringApiModule),
        provideReferenceDataClient(),
        importProvidersFrom(IntegrationApiModule),
        provideSecurityGatewayClient(),
        importProvidersFrom(PerformanceApiModule),
        importProvidersFrom(ConnectorsApiModule),
        { provide: HTTP_INTERCEPTORS, useClass: AppInterceptorService, multi: true },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'fixed', appearance: 'outline'} },
        { provide: GANTT_GLOBAL_CONFIG, useValue: { locale: GanttI18nLocale.enUs} },
        { provide: AddressBASE_PATH, useValue: '/api/address-service' },
        { provide: AccountingBASE_PATH, useValue: '/api/accounting-service' },
        { provide: StatementgBASE_PATH, useValue: '/api/statement-service' },
        { provide: CategorizerBASE_PATH, useValue: '/api/categorizer-service' },
        { provide: ExpenseBASE_PATH, useValue: '/api/expense-service' },
        { provide: AuthenticationBASE_PATH, useValue: '/api/authentication-service' },
        { provide: TransactionBASE_PATH, useValue: '/api/transaction-service' },
        { provide: UIMessageBrokerBASE_PATH, useValue: '/api/ui-message-broker-service' },
        { provide: MonitoringBASE_PATH, useValue: '/api/monitoring-service' },
        { provide: IntegrationBASE_PATH, useValue: '/api/integration-service' },
        { provide: PerformanceBASE_PATH, useValue: '/api/performance-service' },
        { provide: ConnectorsBASE_PATH, useValue: '/api/connectors-service' },
        RequestHandler,
        OverlayService,
        LoadingService,
        UserService,
        AuditService,
        AuthenticationService,
        AppInterceptorService,
        CookieService,
        ApplicationConfigService,
        InitService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
